import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Trophy,
  Lightbulb,
  AlertTriangle,
  Target,
  ArrowRight,
  BookOpen
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
// import api from '../utils/api' // TODO: Update for new API structure
import { useBadgeToast } from '../components/BadgeNotification'
import Footer from '../components/Footer'
import '../styles/lesson.css'

/**
 * LessonScreen Component
 * Displays individual lesson content with interactive elements
 */
const LessonScreen = () => {
  const { lessonId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { showBadgeToasts } = useBadgeToast()
  const [currentSection, setCurrentSection] = useState('introduction')
  const [completed, setCompleted] = useState(false)
  const [lesson, setLesson] = useState(null)
  const [loading, setLoading] = useState(true)

  // Mutation for completing a lesson
  const completeLessonMutation = useMutation({
    mutationFn: ({ lessonId, xpEarned, quizScore }) => 
      api.progress.updateProgress(user.id, { lessonId, xpEarned, quizScore }),
    onSuccess: (data) => {
      // Invalidate progress queries to refresh data
      queryClient.invalidateQueries(['userProgress', user.id])
      queryClient.invalidateQueries(['userBadges', user.id])
      setCompleted(true)
      
      // Show badge notifications if any were earned
      if (data.newBadges && data.newBadges.length > 0) {
        showBadgeToasts(data.newBadges)
      }
    },
    onError: (error) => {
      console.error('Error completing lesson:', error)
      // Still mark as completed locally for better UX
      setCompleted(true)
    }
  })

  // Fetch lesson from API
  const { data: lessonData, isLoading: lessonLoading, error } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      // For now, lessons are identified by their database ID
      // If lessonId is in old format (e.g., "1-1"), convert to lesson ID
      let actualLessonId = lessonId
      if (lessonId.includes('-')) {
        // Map old format to new IDs based on our data
        const lessonMap = {
          '1-1': 1, // Understanding Modern Threats
          '1-2': 2, // Creating Your Family Emergency Plan  
          '1-3': 3, // Building Your 72-Hour Kit
          '2-1': 4, // What is Nuclear Fallout?
        }
        actualLessonId = lessonMap[lessonId] || lessonId
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/lessons/${actualLessonId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('surviveit_token')}`
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch lesson')
      }
      
      return response.json()
    },
    enabled: !!lessonId,
  })

  useEffect(() => {
    if (lessonData) {
      setLesson({
        ...lessonData,
        estimatedTime: 5
      })
    }
    setLoading(lessonLoading)
  }, [lessonData, lessonLoading])

  const { content } = lesson
  
  // Ensure commonMyths has the correct structure
  const myths = content.commonMyths || []
  const formattedMyths = myths.map(m => ({
    myth: m.myth,
    truth: m.reality || m.truth
  }))

  // Navigation between lesson sections
  const sections = [
    { key: 'introduction', title: 'Introduction', icon: Lightbulb },
    { key: 'keyPoints', title: 'Key Points', icon: Target },
    { key: 'didYouKnow', title: 'Did You Know?', icon: CheckCircle },
    { key: 'commonMyths', title: 'Common Myths', icon: AlertTriangle },
    { key: 'actionItems', title: 'Action Items', icon: Trophy }
  ]

  const currentIndex = sections.findIndex(s => s.key === currentSection)
  const isLastSection = currentIndex === sections.length - 1

  // Handle lesson completion
  const handleComplete = () => {
    // Calculate XP based on lesson completion (base 20 XP per lesson)
    const xpEarned = 20
    
    // Update progress in database
    completeLessonMutation.mutate({
      lessonId: lesson.id,
      xpEarned,
      quizScore: null // Will be updated after quiz
    })
    
    // Navigate to quiz after a brief delay
    setTimeout(() => {
      navigate('/quiz/' + lessonId)
    }, 1000)
  }

  // Navigation handlers
  const goToNext = () => {
    if (currentIndex < sections.length - 1) {
      setCurrentSection(sections[currentIndex + 1].key)
    }
  }

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1].key)
    }
  }

  // Render section content
  const renderSectionContent = () => {
    const sectionContent = content[currentSection]

    switch (currentSection) {
      case 'introduction':
        return (
          <div className="lesson-content">
            <p className="text-lg leading-relaxed">{sectionContent}</p>
          </div>
        )

      case 'keyPoints':
        return (
          <div className="lesson-content">
            <ul className="key-points-list">
              {sectionContent.map((point, index) => (
                <li key={index} className="key-point">
                  <div className="point-number">{index + 1}</div>
                  <p>{point}</p>
                </li>
              ))}
            </ul>
          </div>
        )

      case 'didYouKnow':
        return (
          <div className="lesson-content">
            <div className="did-you-know-card">
              <div className="card-header">
                <Lightbulb size={24} />
                <span>Interesting Fact</span>
              </div>
              <p className="text-lg">{sectionContent}</p>
            </div>
          </div>
        )

      case 'commonMyths':
        return (
          <div className="lesson-content">
            <div className="myths-container">
              {formattedMyths.map((item, index) => (
                <div key={index} className="myth-card">
                  <div className="myth-section">
                    <h4 className="myth-label">❌ Myth</h4>
                    <p className="myth-text">{item.myth}</p>
                  </div>
                  <div className="truth-section">
                    <h4 className="truth-label">✅ Truth</h4>
                    <p className="truth-text">{item.truth}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case 'actionItems':
        return (
          <div className="lesson-content">
            <h3 className="action-header">Your Next Steps</h3>
            <div className="action-items">
              {sectionContent.map((item, index) => (
                <div key={index} className="action-item">
                  <div className="action-checkbox">
                    <CheckCircle size={20} />
                  </div>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        )

      default:
        return <div>Content not found</div>
    }
  }

  const renderShell = (body) => (
    <div className="lesson-shell">
      <div className="landing-ambient" />

      {/* Header */}
      <header className="lesson-header">
        <div className="container">
          <motion.div
            className="header-top"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className="back-button"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <div className="lesson-meta">
              <Clock size={16} />
              <span>{lesson?.estimatedTime || 5} min read</span>
            </div>
          </motion.div>
          <motion.div
            className="lesson-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="lesson-badge">
              <BookOpen size={16} />
              <span>{lesson?.moduleTitle || 'Lesson'}</span>
            </div>
            <h1 className="lesson-title">{lesson?.title}</h1>
            <div className="progress-indicators">
              {sections.map((section, index) => (
                <div
                  key={section.key}
                  className={`progress-dot ${currentIndex >= index ? 'active' : ''}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </header>

      {/* Section Navigation */}
      <nav className="section-nav">
        <div className="container">
          <div className="section-tabs">
            {sections.map((section, index) => (
              <motion.button
                key={section.key}
                className={`section-tab ${currentSection === section.key ? 'active' : ''}`}
                onClick={() => setCurrentSection(section.key)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <section.icon size={18} />
                <span>{section.title}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="lesson-main container">
        <motion.div
          className="content-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="section-header-inner">
            <span className="section-step">Step {currentIndex + 1} of {sections.length}</span>
            <h2 className="section-title">{sections[currentIndex].title}</h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {body}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="lesson-navigation">
            <button
              className="btn btn-secondary"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
            >
              <ArrowLeft size={20} />
              Previous
            </button>

            {isLastSection ? (
              <motion.button
                className="btn btn-primary btn-complete"
                onClick={handleComplete}
                disabled={completed || completeLessonMutation.isPending}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {completed ? (
                  <>
                    <CheckCircle size={20} />
                    Completed!
                  </>
                ) : completeLessonMutation.isPending ? (
                  <>
                    Saving Progress...
                  </>
                ) : (
                  <>
                    Complete Lesson
                    <Trophy size={20} />
                  </>
                )}
              </motion.button>
            ) : (
              <motion.button
                className="btn btn-primary"
                onClick={goToNext}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continue
                <ArrowRight size={20} />
              </motion.button>
            )}
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  )

  if (loading) {
    return renderShell(
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading lesson...</p>
      </div>
    )
  }

  if (!lesson) {
    return renderShell(
      <div className="error-state">
        <AlertTriangle size={48} />
        <h2>Lesson not found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    )
  }

  return renderShell(renderSectionContent())
}

export default LessonScreen
